import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

export interface Page<T> { items: T[]; page: number; pageSize: number; total: number }

/** All API calls go through here: same-origin, cookies included, JSON in/out, errors unwrapped. */
export async function apiFetch<T = unknown>(path: string, init: RequestInit & { json?: unknown; query?: Record<string, unknown> } = {}): Promise<T> {
  const { json, query, ...rest } = init;
  let url = path.startsWith("/") ? path : `/api/${path}`;
  if (query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    const s = qs.toString();
    if (s) url += (url.includes("?") ? "&" : "?") + s;
  }
  const headers = new Headers(rest.headers);
  if (json !== undefined) headers.set("content-type", "application/json");
  const res = await fetch(url, { ...rest, headers, credentials: "same-origin", body: json !== undefined ? JSON.stringify(json) : rest.body });
  const text = await res.text();
  let body: unknown = undefined;
  try { body = text ? JSON.parse(text) : undefined; } catch { body = text; }
  if (!res.ok) {
    const b = (body ?? {}) as { error?: string; message?: string; details?: unknown };
    throw new ApiError(res.status, b.error ?? "error", b.message ?? res.statusText, b.details);
  }
  return body as T;
}

export function useApi<T>(key: unknown[], path: string, query?: Record<string, unknown>, options?: Partial<UseQueryOptions<T, ApiError>>) {
  return useQuery<T, ApiError>({ queryKey: [...key, query], queryFn: () => apiFetch<T>(path, { query }), ...options });
}

export function useApiMutation<TVars = unknown, TRes = unknown>(fn: (vars: TVars) => Promise<TRes>, invalidate: unknown[][] = []) {
  const qc = useQueryClient();
  return useMutation<TRes, ApiError, TVars>({
    mutationFn: fn,
    onSuccess: () => { for (const k of invalidate) void qc.invalidateQueries({ queryKey: k }); },
  });
}
